/**
 * Monday.com Authentication Context
 *
 * This context provides zero-friction authentication using monday.com's session tokens
 * instead of requiring users to log in separately. It automatically authenticates
 * users when the app loads within the monday.com interface.
 *
 * Auth flow is broken into 4 visible steps so the loading screen can show progress
 * and errors can be attributed to the correct service (monday.com vs backend vs network).
 */

'use client';

import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef, ReactNode } from 'react';
import mondaySdk from 'monday-sdk-js';
import { createClient } from '@/lib/supabase/client';
import { getSubscriptionTier, type MondaySubscriptionData } from '@/lib/utils/subscription-tier';
import type { ClientOrganization } from '@/lib/db/types';
import {
  type AuthErrorSource,
  type AuthStep,
  type AuthStepId,
  createInitialSteps,
  advanceToStep,
  markStepFailed,
  markAllDone,
  classifyErrorSource,
  isSourceRetryable,
} from '@/lib/auth/auth-steps';
import { isViewOnlyFromToken } from '@/lib/auth/decode-session-token';

// Re-export for consumers
export type { AuthErrorSource, AuthStep, AuthStepId } from '@/lib/auth/auth-steps';

// Initialize monday SDK
const monday = mondaySdk();

// ============================================================================
// Authentication Configuration
// ============================================================================

const AUTH_CONFIG = {
  maxAttempts: 3,
  perAttemptTimeoutMs: 4000,
  retryDelays: [0, 1500, 3000],
};

// ============================================================================
// Legacy type exports (kept for backward compat in tests)
// ============================================================================

/** @deprecated Use AuthErrorSource instead */
export type AuthErrorType =
  | 'timeout'
  | 'sdk_unavailable'
  | 'no_token'
  | 'no_context'
  | 'api_error'
  | 'unknown';

export interface AuthRetryState {
  attempt: number;
  maxAttempts: number;
  isRetrying: boolean;
}

// Type definitions based on monday.com documentation
export interface MondayUser {
  id: number;
  name: string;
  email: string;
  photo_original?: string;
  is_admin?: boolean;
  is_guest?: boolean;
  is_pending?: boolean;
  is_view_only?: boolean;
}

export interface MondayAccount {
  id: number;
  name: string;
  slug: string;
  logo?: string;
  plan?: {
    period: string;
    tier: string;
    version: number;
  };
}

export interface MondayBoard {
  id: number;
  name: string;
  type: string;
  workspace_id?: number;
}

export interface MondaySubscription {
  plan_id: string;
  is_trial: boolean;
  renewal_date: string;
  billing_period: string;
  days_left: number;
  pricing_version: number;
  max_units: number;
  is_pro?: boolean;
}

export interface MondayContext {
  user?: MondayUser;
  account?: MondayAccount;
  board?: MondayBoard;
  boardId?: number;
  boardViewId?: number;
  workspaceId?: number;
  mode?: 'fullScreen' | 'split' | 'popup';
  theme?: string;
  subscription?: MondaySubscription;
  location?: {
    href: string;
    host: string;
    pathname: string;
    search: string;
  };
}

export interface MondayAuthState {
  isLoading: boolean;
  isAuthenticated: boolean;
  sessionToken?: string;
  context?: MondayContext;
  user?: MondayUser;
  account?: MondayAccount;
  subscription?: MondaySubscription;
  organization?: ClientOrganization;
  error?: string;
  errorSource?: AuthErrorSource;
  authSteps?: AuthStep[];
  retryState?: AuthRetryState;
}

interface MondayAuthContextType extends MondayAuthState {
  refreshAuth: () => Promise<void>;
  monday: typeof monday;
}

const MondayAuthContext = createContext<MondayAuthContextType | undefined>(undefined);

/**
 * Helper function to create or get organization in Supabase
 */
async function getOrCreateOrganization(
  account: MondayAccount,
  subscription?: MondaySubscription,
  sessionToken?: string,
  mondayUserId?: number
): Promise<ClientOrganization> {
  try {
    const response = await fetch('/api/organizations', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ account, subscription, sessionToken, mondayUserId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get or create organization');
    }

    const { organization } = await response.json();
    return organization;
  } catch (error) {
    console.error('Error managing organization:', error);
    throw error;
  }
}

/**
 * Wait for a specified duration
 */
function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/** Normalize monday.com theme to 'light' | 'dark' for our CSS. */
function resolveTheme(theme: string | undefined): 'light' | 'dark' {
  return theme && theme !== 'light' ? 'dark' : 'light';
}

interface MondayAuthProviderProps {
  children: ReactNode;
}

export function MondayAuthProvider({ children }: MondayAuthProviderProps) {
  const [authState, setAuthState] = useState<MondayAuthState>({
    isLoading: true,
    isAuthenticated: false,
  });

  // Ref to prevent double initialization from React StrictMode
  const authInitializedRef = useRef(false);

  /**
   * Initialize monday.com authentication with retry logic.
   * Tracks progress through 4 steps so the UI can show which phase is active.
   */
  const initializeAuth = async (attempt: number = 1) => {
    const timeoutMs = AUTH_CONFIG.perAttemptTimeoutMs;
    let currentStep: AuthStepId = 'authenticating';
    let steps = createInitialSteps();

    // Helper to advance to a step and flush state to React
    const goToStep = (stepId: AuthStepId) => {
      currentStep = stepId;
      steps = advanceToStep(steps, stepId);
      setAuthState(prev => ({
        ...prev,
        isLoading: true,
        error: undefined,
        errorSource: undefined,
        authSteps: steps,
        retryState: {
          attempt,
          maxAttempts: AUTH_CONFIG.maxAttempts,
          isRetrying: attempt > 1,
        },
      }));
    };

    try {
      // ── Step 1: Authenticating with monday.com ──
      goToStep('authenticating');

      const sessionTokenResponse = await Promise.race([
        monday.get('sessionToken'),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`Session token request timed out after ${timeoutMs}ms`)), timeoutMs)
        )
      ]) as { data: string };
      const sessionToken = sessionTokenResponse.data;

      if (!sessionToken) {
        throw new Error('No session token received from monday.com');
      }

      const contextResponse = await Promise.race([
        monday.get('context'),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`Context request timed out after ${timeoutMs}ms`)), timeoutMs)
        )
      ]) as { data: MondayContext };

      const context = contextResponse.data;

      if (!context) {
        throw new Error('No context data received from monday.com');
      }

      // Apply theme immediately so the loading screen renders in the correct theme
      if (typeof document !== 'undefined' && context.theme) {
        document.documentElement.setAttribute('data-theme', resolveTheme(context.theme));
      }

      const { user: contextUser, account: contextAccount, subscription } = context;

      if (!contextUser?.id || !contextAccount?.id) {
        throw new Error('Missing user or account ID in context');
      }

      // ── Viewer early-exit ──
      // Detect view-only users from the JWT before making any API calls.
      // Viewers don't have API access, so the GraphQL call below would fail
      // and get misclassified as an API outage. The layout guard in layout.tsx
      // will render ViewOnlyScreen when user.is_view_only is true.
      if (isViewOnlyFromToken(sessionToken)) {
        steps = markAllDone(steps);
        setAuthState({
          isLoading: false,
          isAuthenticated: true,
          sessionToken,
          context,
          user: {
            id: contextUser.id,
            name: contextUser.name || 'Viewer',
            email: contextUser.email || '',
            is_view_only: true,
          },
          account: {
            id: contextAccount.id,
            name: contextAccount.name || '',
            slug: contextAccount.slug || String(contextAccount.id),
          },
          subscription: undefined,
          organization: undefined,
          error: undefined,
          errorSource: undefined,
          authSteps: steps,
          retryState: undefined,
        });
        return;
      }

      const mondayGraphQL = monday.api as (query: string) => Promise<{ data: Record<string, unknown> }>;

      const userQuery = `query {
        me {
          id
          name
          email
          photo_original
          is_admin
          is_guest
          is_pending
          is_view_only
        }
      }`;

      const userResponse = await mondayGraphQL(userQuery);
      const user = userResponse.data?.me as MondayUser | undefined;

      if (!user) {
        throw new Error('Failed to fetch user information from monday.com API');
      }

      // Try to fetch account details via API, fall back to SDK context data
      let account: MondayAccount;
      try {
        const accountQuery = `query {
          account {
            id
            name
            slug
            logo
            plan {
              period
              tier
              version
            }
          }
        }`;
        const accountResponse = await mondayGraphQL(accountQuery);
        const accountData = accountResponse.data?.account as MondayAccount | undefined;
        if (!accountData) throw new Error('No account data');
        account = accountData;
      } catch {
        account = {
          id: contextAccount!.id,
          name: contextAccount!.name,
          slug: contextAccount!.slug || String(contextAccount!.id),
        };
      }

      // ── Step 2: Loading workspace ──
      goToStep('loading_workspace');

      const organization = await getOrCreateOrganization(account, subscription, sessionToken, contextUser.id);

      // ── Step 3: Verifying subscription ──
      goToStep('verifying_subscription');

      // Subscription is already computed in the org response.
      // Brief pause so step is visible in the UI.
      await wait(300);

      // ── Step 4: Preparing dashboard ──
      goToStep('preparing_dashboard');

      // Create Supabase session via server (validates monday token server-side,
      // avoids exposing GoTrue signup/signin endpoints directly from browser)
      const sessionResponse = await fetch('/api/auth/create-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!sessionResponse.ok) {
        const errData = await sessionResponse.json().catch(() => ({ error: { message: 'Session creation failed' } }));
        console.warn('[MondayAuth] create-session error:', errData.error?.message);
        throw new Error(errData.error?.message || 'Failed to create Supabase session');
      }

      const { data: sessionData } = await sessionResponse.json();

      // Set the server-generated session on the browser client
      const supabase = createClient();
      const { error: setSessionError } = await supabase.auth.setSession({
        access_token: sessionData.access_token,
        refresh_token: sessionData.refresh_token,
      });

      if (setSessionError) {
        console.warn('[MondayAuth] setSession error:', setSessionError.message);
      }

      // ── All done ──
      steps = markAllDone(steps);

      // Apply monday.com theme to root element
      if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('data-theme', resolveTheme(context.theme));
      }

      setAuthState({
        isLoading: false,
        isAuthenticated: true,
        sessionToken,
        context,
        user,
        account,
        subscription,
        organization,
        error: undefined,
        errorSource: undefined,
        authSteps: steps,
        retryState: undefined,
      });

      // Initialize token refresh service
      if (typeof window !== 'undefined' && organization && account) {
        const { initializeTokenRefresh, startProactiveRefresh } = await import('@/lib/services/monday/token-refresh');
        initializeTokenRefresh();
        startProactiveRefresh(account.id);
      }

      // Listen for live theme switches from monday.com
      if (typeof window !== 'undefined') {
        monday.listen('context', (res: { data: Partial<MondayContext> }) => {
          if (res.data?.theme) {
            document.documentElement.setAttribute('data-theme', resolveTheme(res.data.theme));
          }
        });
      }

      // Track successful authentication
      if (typeof window !== 'undefined') {
        const { trackUserAction, identifyUser } = await import('@/lib/monitoring');

        identifyUser({
          id: user.id.toString(),
          email: user.email,
          name: user.name,
          orgId: account.id.toString(),
          mondayUserId: user.id,
          mondayAccountId: account.id,
          subscriptionTier: (subscription?.plan_id as 'free' | 'professional' | 'business' | undefined) || 'free'
        });

        trackUserAction('monday_auth_success', {
          user_id: user.id,
          account_id: account.id,
          subscription_plan: subscription?.plan_id,
          is_trial: subscription?.is_trial,
          authentication_method: 'session_token',
          attempts: attempt,
        });
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorSource = classifyErrorSource(error, currentStep);
      const shouldRetry = attempt < AUTH_CONFIG.maxAttempts && isSourceRetryable(errorSource);

      console.error(`[Auth] Attempt ${attempt}/${AUTH_CONFIG.maxAttempts} failed (${errorSource}):`, errorMessage);

      if (shouldRetry) {
        const delay = AUTH_CONFIG.retryDelays[attempt] || 3000;

        // Show failed step briefly, then retry
        steps = markStepFailed(steps, currentStep);
        setAuthState(prev => ({
          ...prev,
          authSteps: steps,
          retryState: {
            attempt,
            maxAttempts: AUTH_CONFIG.maxAttempts,
            isRetrying: true,
          },
        }));

        await wait(delay);

        return initializeAuth(attempt + 1);
      }

      // Final failure — no more retries
      steps = markStepFailed(steps, currentStep);

      // Track authentication failure
      if (typeof window !== 'undefined') {
        const { captureError } = await import('@/lib/monitoring');
        captureError(error instanceof Error ? error : new Error('Monday auth failed'), {
          authentication_method: 'session_token',
          step: currentStep,
          error_source: errorSource,
          total_attempts: attempt,
        });
      }

      setAuthState({
        isLoading: false,
        isAuthenticated: false,
        error: errorMessage,
        errorSource,
        authSteps: steps,
        retryState: {
          attempt,
          maxAttempts: AUTH_CONFIG.maxAttempts,
          isRetrying: false,
        },
      });
    }
  };

  /**
   * Refresh authentication data (memoized to prevent unnecessary re-renders)
   */
  const refreshAuth = useCallback(async () => {
    await initializeAuth();
  }, []);

  // Initialize authentication on mount
  useEffect(() => {
    // Prevent double initialization from React StrictMode
    if (authInitializedRef.current) {
      return;
    }
    authInitializedRef.current = true;

    // Skip auth for public pages that don't need monday.com context
    // (e.g., /how-to-use is embedded in the marketplace listing iframe
    // where the monday SDK isn't available, causing session token timeouts)
    if (typeof window !== 'undefined') {
      const publicPaths = ['/how-to-use', '/auth-error'];
      const currentPath = window.location.pathname;
      if (publicPaths.some(p => currentPath.startsWith(p))) {
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          error: undefined,
        });
        return;
      }
    }

    // Only initialize if we're in a monday.com context (iframe)
    if (typeof window !== 'undefined') {
      const isDevelopment = process.env.NODE_ENV === 'development';
      const bypassAuth = isDevelopment && process.env.NEXT_PUBLIC_BYPASS_AUTH === 'true';

      const isInIframe = window.self !== window.top;

      if (isInIframe) {
        initializeAuth();
      } else if (bypassAuth) {
        console.warn('Development mode: Authentication bypassed for local testing');
        document.documentElement.setAttribute('data-theme', 'light');
        setAuthState({
          isLoading: false,
          isAuthenticated: true,
          user: {
            id: 12345,
            name: 'Dev User',
            email: 'dev@taskloops.local'
          },
          account: {
            id: 67890,
            name: 'Dev Organization',
            slug: 'dev-org'
          },
          organization: {
            id: 'dev-org-id',
            monday_account_id: 67890,
            subscription_tier: 'free',
            settings: {},
            oauth_token_expires_at: null,
            session_token_expires_at: null,
            session_token_updated_at: null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            hasOAuthToken: true,
            oauthTokenStatus: 'valid',
          },
          sessionToken: 'dev-session-token',
          error: undefined
        });
      } else {
        console.warn('Not running in monday.com context - authentication skipped');
        setAuthState({
          isLoading: false,
          isAuthenticated: false,
          error: 'App must be run within monday.com interface'
        });
      }
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<MondayAuthContextType>(() => ({
    ...authState,
    refreshAuth,
    monday,
  }), [authState, refreshAuth]);

  return (
    <MondayAuthContext.Provider value={contextValue}>
      {children}
    </MondayAuthContext.Provider>
  );
}

/**
 * Hook to use monday.com authentication
 */
export function useMondayAuth() {
  const context = useContext(MondayAuthContext);
  if (context === undefined) {
    throw new Error('useMondayAuth must be used within a MondayAuthProvider');
  }
  return context;
}

/**
 * Hook to get the monday SDK instance
 */
export function useMonday() {
  const { monday } = useMondayAuth();
  return monday;
}

/**
 * Utility function to check if user has a paid subscription
 */
export function useIsPaidUser() {
  const { subscription } = useMondayAuth();
  return subscription && subscription.plan_id !== 'free' && !subscription.is_trial;
}

/**
 * Utility function to check subscription tier
 */
export function useSubscriptionTier() {
  const { subscription, organization } = useMondayAuth();

  // Use organization tier from database if available (most accurate)
  if (organization) {
    return organization.subscription_tier;
  }

  // Fallback to computing from monday.com subscription
  return getSubscriptionTier(subscription as MondaySubscriptionData | null | undefined);
}

/**
 * Utility hook to get the current organization
 */
export function useOrganization() {
  const { organization } = useMondayAuth();
  return organization;
}
